// Helpers for Admin → Campaigns (no React).

export const STATUS_LABELS = Object.freeze({
    DRAFT: 'Draft',
    SCHEDULED: 'Scheduled',
    SENDING: 'Sending',
    SENT: 'Sent',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
});

export const EMPTY_AUDIENCE = Object.freeze({
    coins: 'any',
    inactiveDays: null,
    foundingCollector: 'any',
    joinedFrom: null,
    joinedTo: null,
    onboarding: 'any',
    emailVerified: 'any',
    signupMethods: [],
    platforms: [],
    sources: [],
    genreIds: [],
});

export const CAMPAIGN_TYPE_LABELS = Object.freeze({
    promotional: 'Promotional',
    informational: 'Informational',
});

export const EMPTY_CAMPAIGN = Object.freeze({
    name: '',
    campaignType: 'promotional',
    subject: '',
    previewText: '',
    fromName: 'Aluvo',
    fromEmail: '',
    replyTo: '',
    headline: '',
    body: '',
    heroImageUrl: '',
    heroImageAlt: '',
    heroLinkUrl: '',
    ctaText: '',
    ctaUrl: '',
    secondaryText: '',
    footerText: '',
    audience: EMPTY_AUDIENCE,
});

const FORM_KEYS = Object.keys(EMPTY_CAMPAIGN).filter((k) => k !== 'audience');

/** Campaign API object → editable form state. */
export function campaignToForm(campaign) {
    const form = { ...EMPTY_CAMPAIGN };
    if (!campaign) return form;
    for (const key of FORM_KEYS) form[key] = campaign[key] ?? '';
    form.campaignType = campaign.campaignType || EMPTY_CAMPAIGN.campaignType;
    form.audience = { ...EMPTY_AUDIENCE, ...(campaign.audience || {}) };
    return form;
}

/** Form state → API payload (empty strings become null so fields can be cleared). */
export function formToPayload(form) {
    const payload = {};
    for (const key of FORM_KEYS) {
        const value = typeof form[key] === 'string' ? form[key].trim() : form[key];
        payload[key] = value === '' ? null : value;
    }
    payload.fromName = payload.fromName || 'Aluvo';
    payload.campaignType = payload.campaignType || 'promotional';
    payload.audience = form.audience;
    return payload;
}

export function formsEqual(a, b) {
    return JSON.stringify(formToPayload(a)) === JSON.stringify(formToPayload(b));
}

export function browserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

export const COMMON_TIME_ZONES = Object.freeze([
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'America/Toronto',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
    'UTC',
]);

function zoneOffsetMs(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
    const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
    return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * Wall-clock date + time in `timeZone` → UTC Date. Handles DST by
 * re-evaluating the offset at the computed instant.
 * @param {string} date 'YYYY-MM-DD'
 * @param {string} time 'HH:mm'
 */
export function zonedTimeToUtc(date, time, timeZone) {
    if (!date || !time) return null;
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);
    if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
    const naive = Date.UTC(y, m - 1, d, hh, mm);
    let guess = naive - zoneOffsetMs(new Date(naive), timeZone);
    guess = naive - zoneOffsetMs(new Date(guess), timeZone);
    return new Date(guess);
}

export function formatDateTime(value, timeZone) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    try {
        return date.toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            ...(timeZone ? { timeZone, timeZoneName: 'short' } : {}),
        });
    } catch {
        return date.toLocaleString();
    }
}

export function relativeTime(value, now = Date.now()) {
    if (!value) return '';
    const seconds = Math.round((now - new Date(value).getTime()) / 1000);
    if (seconds < 45) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    return formatDateTime(value);
}

export function formatPct(value) {
    return value === null || value === undefined ? '' : `${value}%`;
}

/** Field errors from an ApiError payload ({ details: { fields } }). */
export function fieldErrorsOf(err) {
    const fields = err?.payload?.details?.fields;
    if (!fields || typeof fields !== 'object') return {};
    const map = {
        preview_text: 'previewText', from_name: 'fromName', from_email: 'fromEmail', reply_to: 'replyTo', hero_image_url: 'heroImageUrl',
        hero_image_alt: 'heroImageAlt', hero_link_url: 'heroLinkUrl', campaign_type: 'campaignType', cta_text: 'ctaText', cta_url: 'ctaUrl', secondary_text: 'secondaryText', footer_text: 'footerText',
    };
    return Object.fromEntries(Object.entries(fields).map(([k, v]) => [map[k] || k, v]));
}

// ── Pre-send validation (the server report is the single source of truth) ──

/** Errors that block sending. `test_required` is shown by the test panel instead. */
export function blockingErrors(validation) {
    return (validation?.errors || []).filter((e) => e.code !== 'test_required');
}

export function missingLinksError(validation) {
    return (validation?.errors || []).find((e) => e.code === 'no_promotional_links') || null;
}

export function testStatusLabel(test) {
    if (!test || test.status === 'none') return 'No test sent yet';
    return test.status === 'current' ? 'Current — sent after your latest edit' : 'Outdated — the campaign changed after this test';
}

/** Validation report carried on a server error (422 / 409 from schedule or send). */
export function validationOf(err) {
    return err?.payload?.details?.validation || null;
}
